import React, { memo, useCallback } from 'react';
import { useObservable } from 'react-use-observable';

import { Button } from 'antd';

import { Formik, Form, Field } from 'formik';
import type { FormikProps } from 'formik';
import { FilterActions } from 'v2/components/Shared/FilterActions';
import { FilterPanel } from 'v2/components/Shared/FilterPanel';
import { SelectFormField } from 'v2/components/Shared/FormFields/SelectFormField';
import { TextFormField } from 'v2/components/Shared/FormFields/TextFormField';
import type { IFilter } from 'v2/interfaces/whitelist';
import whitelistService, { InitialFilter } from 'v2/services/whitelist';

import { Icon } from '@eduzz/ui-antd-theme';

import FilterSchema from './validations';

const Filter = () => {
  const [isLoading] = useObservable(() => whitelistService.isLoading(), []);

  const CustomerTypeOptions = [
    {
      label: 'Nome',
      value: 'customer_name'
    },
    {
      label: 'E-mail',
      value: 'customer_email'
    },
    {
      label: 'CPF/CNPJ',
      value: 'customer_document'
    }
  ];

  const onSubmit = useCallback((values: IFilter, { setSubmitting, resetForm }: FormikProps<IFilter>) => {
    const newFilter: any = {};
    newFilter[values.type] = values[values.type];
    whitelistService.setFilter(newFilter);
    resetForm({ values });
    setSubmitting(false);
  }, []);

  return (
    <Formik initialValues={InitialFilter} validationSchema={FilterSchema} onSubmit={onSubmit}>
      {({ values, isValid, isSubmitting, resetForm, setFieldValue }) => {
        const onChangeCustomerType = (e: React.ChangeEvent<HTMLInputElement>) => {
          const v = e.target.value;
          setFieldValue('customer_name', '');
          setFieldValue('customer_email', '');
          setFieldValue('customer_document', '');
          setFieldValue('type', v);
        };

        const isCleanDisabled = isSubmitting || isLoading;
        const isFilterDisabled = !isValid || isSubmitting || isLoading;

        return (
          <Form id='whitelist-form'>
            <FilterPanel id='whitelist-filter-panel'>
              <div className='grid grid-cols-1 items-end gap-4 md:grid-cols-12'>
                <div className='md:col-span-3'>
                  <Field
                    id='type'
                    name='type'
                    label='Cliente'
                    fullWidth
                    options={CustomerTypeOptions}
                    component={SelectFormField}
                    onChange={onChangeCustomerType}
                  />
                </div>
                <div className='md:col-span-5'>
                  <Field
                    id={values.type}
                    name={values.type}
                    component={TextFormField}
                    fullWidth
                    label={
                      values.type === 'customer_document'
                        ? 'CPF/CNPJ'
                        : values.type === 'customer_email'
                          ? 'E-mail'
                          : 'Nome'
                    }
                    placeholder={
                      values.type === 'customer_name'
                        ? 'Insira o nome'
                        : values.type === 'customer_email'
                          ? 'Insira o e-mail'
                          : 'Insira o CPF/CNPJ'
                    }
                    mask={values.type === 'customer_document' ? 'document' : null}
                  />
                </div>
                <div className='md:col-span-4'>
                  <FilterActions>
                    <Button
                      type='text'
                      disabled={isCleanDisabled}
                      loading={isLoading || isSubmitting}
                      onClick={() =>
                        resetForm({
                          values: {
                            type: values.type,
                            customer_name: '',
                            customer_document: '',
                            customer_email: ''
                          }
                        })
                      }
                    >
                      Limpar
                    </Button>
                    <Button
                      type='primary'
                      disabled={isFilterDisabled}
                      htmlType='submit'
                      loading={isLoading || isSubmitting}
                      icon={<Icon.Funnel />}
                    >
                      Filtrar
                    </Button>
                  </FilterActions>
                </div>
              </div>
            </FilterPanel>
          </Form>
        );
      }}
    </Formik>
  );
};

export default memo(Filter);
